import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2, Truck } from 'lucide-react';
import {
  useSupplier,
  useSupplierCategories,
  useUpdateSupplier,
  useDeleteSupplier,
} from '../hooks/useSuppliers';
import { useInvoices } from '../hooks/useInvoices';
import { useSupplierStats } from '../hooks/useEntityStats';
import { supplierCategoryLabel, type SupplierInput } from '../lib/suppliersApi';
import { FinancialStatsSection } from '../components/financials/FinancialStatsSection';
import { FadeIn } from '../components/ui/Skeleton';
import { SupplierFormModal } from '../components/suppliers/SupplierFormModal';
import { SupplierResourcesTab } from '../components/suppliers/SupplierResourcesTab';

const TABS = ['overview', 'invoices', 'resources'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Prezentare generală',
  invoices: 'Facturi',
  resources: 'Resurse',
};

export function SupplierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: supplier, isLoading, isError } = useSupplier(id);
  const { data: categoryDefs } = useSupplierCategories();
  const updateMutation = useUpdateSupplier(id ?? '');
  const deleteMutation = useDeleteSupplier();
  const [showEdit, setShowEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const initialTab = (window.location.hash.replace('#', '') as Tab) || 'overview';
  const [tab, setTab] = useState<Tab>(TABS.includes(initialTab) ? initialTab : 'overview');

  function selectTab(next: Tab) {
    setTab(next);
    window.location.hash = next;
  }

  async function handleUpdate(input: SupplierInput) {
    await updateMutation.mutateAsync(input);
    setShowEdit(false);
  }

  async function handleDelete() {
    if (!id) return;
    await deleteMutation.mutateAsync(id);
    navigate('/suppliers');
  }

  if (isLoading) {
    return <div className="p-8 text-gray-500">Se încarcă…</div>;
  }
  if (isError || !supplier) {
    return (
      <div className="p-8">
        <p className="text-red-600">Nu s-a putut încărca furnizorul.</p>
        <Link to="/suppliers" className="mt-2 inline-block text-brand-700 hover:underline">
          Înapoi la furnizori
        </Link>
      </div>
    );
  }

  const rows: { label: string; value: string | null }[] = [
    { label: 'CUI', value: supplier.cui },
    { label: 'IBAN', value: supplier.iban },
    { label: 'Persoană de contact', value: supplier.contactPerson },
    { label: 'Email', value: supplier.email },
    { label: 'Telefon', value: supplier.phone },
    { label: 'Adresă', value: supplier.address },
    { label: 'Categorie', value: supplierCategoryLabel(supplier.category, categoryDefs) },
  ];

  return (
    <FadeIn>
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <Link
          to="/suppliers"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={16} />
          Furnizori
        </Link>

        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Truck className="text-brand-600" size={24} />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{supplier.name}</h1>
                {supplier.isRecurring && (
                  <span className="inline-block rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                    Recurent
                  </span>
                )}
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    supplier.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {supplier.isActive ? 'Activ' : 'Inactiv'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Pencil size={16} />
              Editează
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Trash2 size={16} />
              Șterge
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex gap-6 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => selectTab(t)}
                className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                  tab === t
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-6">
          <FadeIn>
            {tab === 'overview' && (
              <div className="space-y-6">
                <SupplierStatsSection supplierId={supplier.id} />
                <div className="max-w-2xl overflow-hidden rounded-lg border border-gray-200">
                  <dl className="divide-y divide-gray-100">
                    {rows.map((r) => (
                      <div key={r.label} className="grid grid-cols-3 gap-2 px-4 py-3">
                        <dt className="text-sm font-medium text-gray-500">{r.label}</dt>
                        <dd className="col-span-2 text-sm text-gray-900">{r.value || '—'}</dd>
                      </div>
                    ))}
                    {supplier.notes && (
                      <div className="px-4 py-3">
                        <dt className="mb-1 text-sm font-medium text-gray-500">Notițe</dt>
                        <dd className="whitespace-pre-wrap text-sm text-gray-900">
                          {supplier.notes}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            )}
            {tab === 'invoices' && <SupplierInvoicesTab supplierName={supplier.name} />}
            {tab === 'resources' && id && <SupplierResourcesTab supplierId={id} />}
          </FadeIn>
        </div>

        {showEdit && (
          <SupplierFormModal
            title="Editează furnizorul"
            initial={supplier}
            onClose={() => setShowEdit(false)}
            onSubmit={handleUpdate}
            submitting={updateMutation.isPending}
          />
        )}

        {confirmDelete && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setConfirmDelete(false);
            }}
          >
            <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
              <h2 className="text-lg font-semibold text-gray-900">Ștergi furnizorul?</h2>
              <p className="mt-2 text-sm text-gray-600">
                „{supplier.name}” va fi dezactivat și nu va mai fi folosit pentru potrivirea
                automată. Îl poți reactiva ulterior prin editare.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Anulează
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={deleteMutation.isPending}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Șterge
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </FadeIn>
  );
}

// --- Supplier financial stats (overview tab) -------------------------------

function SupplierStatsSection({ supplierId }: { supplierId: string }) {
  const { data, isLoading } = useSupplierStats(supplierId);
  return <FinancialStatsSection stats={data} isLoading={isLoading} direction="out" />;
}

// --- Supplier invoices tab ------------------------------------------------

function SupplierInvoicesTab({ supplierName }: { supplierName: string }) {
  const { data, isLoading } = useInvoices({ type: 'OUTCOME' });
  const invoices = (data?.data ?? []).filter((i) => i.vendor === supplierName);

  if (isLoading) {
    return <p className="py-8 text-center text-sm text-gray-500">Se încarcă facturile…</p>;
  }

  if (invoices.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">Nicio factură de la acest furnizor.</p>
    );
  }

  const fmt = (v: number) =>
    v.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Factură</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Data</th>
            <th className="px-3 py-2 text-right font-medium text-gray-600">Sumă</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {invoices.map((inv) => (
            <tr key={inv.id} className="hover:bg-gray-50">
              <td className="px-3 py-2 font-medium text-gray-900">{inv.invoiceNumber}</td>
              <td className="px-3 py-2 text-gray-600">{inv.issueDate}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                {fmt(inv.amountTotal)} {inv.currency}
              </td>
              <td className="px-3 py-2">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    inv.status === 'PAID'
                      ? 'bg-green-100 text-green-700'
                      : inv.overdue
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {inv.status === 'PAID' ? 'Plătit' : inv.overdue ? 'Restant' : 'În așteptare'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
